import {
  getWebInstrumentations,
  initializeFaro,
  type Faro,
  type TransportItem,
} from '@grafana/faro-web-sdk'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'

let faroInstance: Faro | null = null

/**
 * Patterns to redact from URLs and strings for GDPR compliance
 */
const PII_PATTERNS = [
  // Email addresses
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  // Phone numbers - supports international formats (US, German/European, etc.)
  // Matches various formats:
  // - International: +49 176 631231123, +1-234-567-8900
  // - German national: 0176 631231123, 030 12345678
  // - US formats: (123) 456-7890, 123-456-7890, 123.456.7890
  // Avoids false positives like IP addresses (192.168.1.1) or version numbers
  /(?:\+\d{1,3}[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}|0\d{2,4}[-.\s]?\d{3,10}|\(\d{2,4}\)[-.\s]?\d{3,4}[-.\s]?\d{4}|\d{3}[-.\s]\d{3}[-.\s]\d{4})\b/g,
  // JWT tokens
  /eyJ[a-zA-Z0-9_-]*\.eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g,
  // Authorization headers
  /Bearer\s+[a-zA-Z0-9._-]+/gi,
]

/**
 * Redact PII from a string
 */
function redactPII(str: string): string {
  let result = str
  for (const pattern of PII_PATTERNS) {
    result = result.replace(pattern, '[REDACTED]')
  }
  return result
}

/**
 * Redact UUIDs only from query parameters in URLs
 * Preserves UUIDs in URL paths for debugging purposes
 */
function redactUUIDsFromQueryParams(url: string): string {
  try {
    const urlObj = new URL(url)
    // Redact UUIDs only in search params
    const params = new URLSearchParams(urlObj.search)
    params.forEach((value, key) => {
      // Use replace directly - no need to test first
      const redacted = value.replace(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
        '[REDACTED]'
      )
      if (redacted !== value) {
        params.set(key, redacted)
      }
    })
    urlObj.search = params.toString()
    return urlObj.toString()
  } catch {
    // If URL parsing fails, return as-is (not a valid URL)
    return url
  }
}

function beforeSend(item: TransportItem): TransportItem | null {
  // Redact PII from meta information (URLs, view names).
  // Payloads (error messages, stack traces) are not redacted here to preserve
  // debuggability — ensure no PII is included in error messages at the source.
  if (item.meta) {
    if (item.meta.page?.url) {
      // First redact general PII
      item.meta.page.url = redactPII(item.meta.page.url)
      // Then selectively redact UUIDs only from query params
      item.meta.page.url = redactUUIDsFromQueryParams(item.meta.page.url)
    }
    if (item.meta.view?.name) {
      item.meta.view.name = redactPII(item.meta.view.name)
    }
  }

  return item
}

/**
 * Gets Faro collector URL from environment
 * Only initializes in non-development environments when URL is provided
 */
function getFaroCollectorUrl(): string | undefined {
  // Check runtime config first (containerized deployment)
  if (typeof window !== 'undefined' && (window as any).ENV) {
    const runtimeValue = (window as any).ENV['VITE_FARO_COLLECTOR_URL']
    if (runtimeValue && runtimeValue !== 'placeholder') {
      return runtimeValue
    }
  }

  // Check build-time environment variables
  const buildTimeValue = import.meta.env.VITE_FARO_COLLECTOR_URL
  if (buildTimeValue && buildTimeValue !== 'placeholder') {
    return buildTimeValue
  }

  return undefined
}

/**
 * Gets the app version from package.json or environment
 */
function getAppVersion(): string {
  if (typeof window !== 'undefined' && (window as any).ENV?.VITE_APP_VERSION) {
    return (window as any).ENV.VITE_APP_VERSION
  }
  return import.meta.env.VITE_APP_VERSION || '1.0.0'
}

/**
 * Gets the environment name (development, demo, production)
 */
function getEnvironment(): string {
  if (typeof window !== 'undefined' && (window as any).ENV?.VITE_ENVIRONMENT) {
    return (window as any).ENV.VITE_ENVIRONMENT
  }
  return import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'development'
}

/**
 * Initialize Grafana Faro for frontend observability
 * Only initializes when VITE_FARO_COLLECTOR_URL is set (demo/production)
 */
export function initFaro(): Faro | null {
  const collectorUrl = getFaroCollectorUrl()

  // Skip initialization if no collector URL (local development)
  if (!collectorUrl) {
    console.debug('[Faro] Skipping initialization - no collector URL configured')
    return null
  }

  // Don't reinitialize
  if (faroInstance) {
    return faroInstance
  }

  try {
    faroInstance = initializeFaro({
      url: collectorUrl,
      app: {
        name: 'Audavo Frontend',
        version: getAppVersion(),
        environment: getEnvironment(),
      },
      instrumentations: [
        // Default web instrumentations (console, errors, web vitals, etc.)
        ...getWebInstrumentations({
          captureConsole: true,
          captureConsoleDisabledLevels: [], // Capture all levels
        }),
        // Tracing for end-to-end HTTP request visibility
        new TracingInstrumentation({
          instrumentationOptions: {
            // Don't propagate trace context to external APIs (prevents token leakage)
            propagateTraceHeaderCorsUrls: [
              new RegExp(`^${window.location.origin}`),
            ],
            // Ignore SSE/streaming endpoints, Auth0, and Faro collector itself
            ignoreUrls: [
              /\/api\/v1\/stream\//,
              /\/api\/v1\/sse/,
              /\.auth0\.com/,
              /EventSource/,
              /faro-collector.*\.grafana\.net/,
            ],
          },
        }),
      ],
      // GDPR: Filter PII before sending to Grafana
      beforeSend,
      // Don't track user - we're not setting any user identification
      sessionTracking: {
        persistent: false, // Don't persist session across page loads
      },
    })

    console.debug('[Faro] Initialized successfully', {
      environment: getEnvironment(),
      version: getAppVersion(),
    })

    return faroInstance
  } catch (error) {
    console.error('[Faro] Failed to initialize:', error)
    return null
  }
}

/**
 * Get the Faro instance (may be null if not initialized)
 */
export function getFaro(): Faro | null {
  return faroInstance
}

/**
 * Log an error to Faro (with fallback to console)
 */
export function logErrorToFaro(
  error: Error,
  context?: Record<string, string>
): void {
  if (faroInstance) {
    faroInstance.api.pushError(error, {
      context,
    })
  } else {
    // Fallback to console in development
    console.error('[Error]', error, context)
  }
}

/**
 * Log a custom event to Faro
 */
export function logEventToFaro(
  name: string,
  attributes?: Record<string, string>
): void {
  if (faroInstance) {
    faroInstance.api.pushEvent(name, attributes)
  }
}
